import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { rememberDestination, consumeDestination } from "./postLoginRedirect";
import LandingPage from "../operations/pages/LandingPage/LandingPage";
import OperationHome from "../operations/pages/OperationHome/OperationHome";
import DetailsScreen from "../operations/pages/DetailsPage/DetailsScreen";
import DashboardScreen from "../operations/pages/DashboardScreen/DashboardScreen";
import AgentHome from "../agent/pages/AgentHome/AgentHome";
import AgentImprovement from "../agent/pages/AgentImprovement/AgentImprovement";
import LearningLink from "../agent/components/Learning/LearningLink";
import PerformanceDashboardScreen from "../operations/pages/PerformanceDashboardScreen/PerformanceDashboardScreen";
import AgentDashboard from "../operations/pages/AgentGuideDashboard/AgentDashboard";
import AnalyticsScreen from "../operations/pages/AnalyticsScreen/AnalyticsScreen";
import MontenidoUsage from "../operations/pages/MontenidoUsage/MontenidoUsage";
import ReleaseNotes from "../operations/pages/ReleaseNotes/ReleaseNotes";

// Release Notes is enabled only where its API is configured. Only .env.dev
// defines REACT_APP_RELEASE_NOTES_API, so the feature is live in dev and absent
// from QA/Production builds until that var is added there deliberately.
const isReleaseNotesEnabled = Boolean(process.env.REACT_APP_RELEASE_NOTES_API);

/**
 * Where to land once signed in. Normally /landing, but if the visitor was
 * bounced off a real URL on the way in (an emailed deep link, a bookmark),
 * this returns them to it. Runs on the catch-all, which is where the app sits
 * immediately after login.
 */
const LandingRedirect = () => {
  const target = consumeDestination();
  return <Navigate to={target || "/landing"} replace />;
};

export const ProtectedRoutes = () => {
  const authToken = localStorage.getItem("authToken");
  const location = useLocation();

  const isTokenExpired = () => {
    if (!authToken) return true;

    try {
      const payload = JSON.parse(atob(authToken.split(".")[1]));
      return Date.now() > payload.exp * 1000;
    } catch {
      return true;
    }
  };

  const tokenExpired = isTokenExpired();

  // If token missing or expired → redirect to login, keeping hold of where
  // they were so an expired session doesn't cost them the page they wanted.
  if (!authToken || tokenExpired) {
    localStorage.removeItem("authToken");
    rememberDestination(location.pathname, location.search);
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/operationhome" element={<OperationHome />} />
      <Route
        path="/detailsPage/:memberId/:callId"
        element={<DetailsScreen />}
      />
      <Route path="/dashboardPage" element={<DashboardScreen />} />
      <Route
        path="/performancedashboardPage"
        element={<PerformanceDashboardScreen />}
      />
      <Route path="/agentGuideDashboard" element={<AgentDashboard />} />
      <Route path="/analytics" element={<AnalyticsScreen />} />
      <Route path="/montenido-usage" element={<MontenidoUsage />} />
      {/* Gated on the same flag as the "What's New" menu entry in Navbar.js, so
          the page can't be reached by typing the URL in an environment where
          the feature isn't meant to exist yet. Unknown routes fall through to
          the catch-all below and redirect to /landing. */}
      {isReleaseNotesEnabled && (
        <Route path="/release-notes" element={<ReleaseNotes />} />
      )}
      <Route path="/agenthome" element={<AgentHome />} />
      <Route
        path="/operationhome/improvementInsight"
        element={
          <AgentImprovement dataAccessor="recommendations" cardHeader="Agent" />
        }
      />
      <Route
        path="/operationhome/processInsight"
        element={
          <AgentImprovement
            dataAccessor="processImprovement"
            cardHeader="Process"
          />
        }
      />
      <Route path="/learninglink" element={<LearningLink />} />

      {/* Catch-all. Also where the app lands immediately after signing in (the
          URL is still "/"), so this is what returns an emailed deep link to
          its destination instead of dropping everyone on /landing. */}
      <Route path="*" element={<LandingRedirect />} />
    </Routes>
  );
};
